import { Observable } from 'rxjs';
import { PaginatedRequest, PaginatedResponse } from '../pagination/types';

/**
 * Abstract class for basic CRUD operations.
 * @param Type The type of object.
 * @param IdType The type of the object identifier.
 * @param FilterParamsType The type of filter parameters used for searching.
 * @param ListItemType The type of items listed.
 * @param CreateItemType The type of items to register.
 *
 */
export abstract class BasicCrudService<
  Type,
  IdType = string,
  FilterParamsType = PaginatedRequest,
  ListItemType = Type,
  CreateItemType = Type
> {
  /**
   * Get an object by its identifier.
   * @param id The identifier of the object.
   * @returns An observable emitting the retrieved object.
   */
  abstract getById(id: IdType): Observable<Type>;

  /**
   * Get a paginated list of objects.
   * @param filter Filter parameters for searching.
   * @returns An observable emitting the paginated list of objects.
   */
  abstract findAll(
    filter?: FilterParamsType
  ): Observable<PaginatedResponse<ListItemType>>;

  /**
   * Update an object.
   * @param id The identifier of the object to update.
   * @param data The information to update the object.
   * @returns An observable emitting void once the update is completed.
   */
  abstract update(id: IdType, data: Partial<Omit<Type, 'id'>>): Observable<void>;

  /**
   * Create a new object.
   * @param data The information of the new object.
   * @returns An observable emitting void once the object is created.
   */
  abstract create(data: Omit<CreateItemType, 'id'>): Observable<void | number>;

  /**
   * Remove an object.
   * @param id The identifier of the object to remove.
   * @returns An observable emitting void once the object is removed.
   */
  abstract delete(id: IdType): Observable<void>;
}
